const COMPLETED_IMAGE_TTL_MS = 6 * 60 * 60 * 1000;
const FAILURE_COOLDOWN_MS = 30 * 1000;
const MAX_REGISTRY_ENTRIES = 800;

const completedImages = new Map();
const failedImages = new Map();

const normalizeKey = (value = "") => String(value || "").trim();

const rememberBounded = (registry, key, value) => {
  registry.delete(key);
  registry.set(key, value);
  while (registry.size > MAX_REGISTRY_ENTRIES) {
    registry.delete(registry.keys().next().value);
  }
};

export const markImageWorkCompleted = (url, now = Date.now()) => {
  const key = normalizeKey(url);
  if (!key) return;
  rememberBounded(completedImages, key, Number(now));
  failedImages.delete(key);
};

export const hasCompletedImageWork = (url, now = Date.now()) => {
  const key = normalizeKey(url);
  if (!key) return false;
  const completedAt = Number(completedImages.get(key));
  if (!Number.isFinite(completedAt) || Number(now) - completedAt > COMPLETED_IMAGE_TTL_MS) {
    completedImages.delete(key);
    return false;
  }
  return true;
};

export const markImageWorkFailed = (url, now = Date.now()) => {
  const key = normalizeKey(url);
  if (!key) return;
  const previous = failedImages.get(key);
  const attempts = Math.min(4, Math.max(0, Number(previous?.attempts) || 0) + 1);
  rememberBounded(failedImages, key, {
    attempts,
    retryAt: Number(now) + FAILURE_COOLDOWN_MS * attempts,
  });
};

export const getImageWorkRetryDelay = (url, now = Date.now()) => {
  const key = normalizeKey(url);
  const failure = key ? failedImages.get(key) : null;
  if (!failure) return 0;
  const delay = Math.max(0, Number(failure.retryAt) - Number(now));
  if (!delay) failedImages.delete(key);
  return delay;
};

export const resolveImageWorkConcurrency = () => {
  if (typeof navigator === "undefined") return 4;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const effectiveType = String(connection?.effectiveType || "").toLowerCase();
  if (connection?.saveData || effectiveType === "slow-2g" || effectiveType === "2g") return 2;
  if (effectiveType === "3g") return 3;
  if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)")?.matches) return 4;
  return 6;
};

export const resolveImageWorkRootMargin = () => {
  if (typeof navigator === "undefined") return "600px 0px";
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const effectiveType = String(connection?.effectiveType || "").toLowerCase();
  return connection?.saveData || effectiveType.includes("2g") ? "200px 0px" : "600px 0px";
};

export const createImageWorkController = ({
  maxConcurrent = resolveImageWorkConcurrency,
  workTimeoutMs = 30 * 1000,
} = {}) => {
  const queues = { priority: [], standard: [] };
  const queuedGroups = new Map();
  const activeGroups = new Map();
  let activeCount = 0;
  let waiterSequence = 0;

  const concurrency = () => Math.max(
    1,
    Number(typeof maxConcurrent === "function" ? maxConcurrent() : maxConcurrent) || 1,
  );

  const removeQueuedGroup = (group) => {
    const queue = queues[group.priority ? "priority" : "standard"];
    const index = queue.indexOf(group);
    if (index >= 0) queue.splice(index, 1);
    queuedGroups.delete(group.key);
  };

  const finishGroup = (group) => {
    if (activeGroups.get(group.key) !== group) return;
    clearTimeout(group.timeoutId);
    activeGroups.delete(group.key);
    activeCount = Math.max(0, activeCount - 1);
    drain();
  };

  const releaseWaiter = (group, waiter) => {
    if (waiter.released) return;
    waiter.released = true;
    group.waiters.delete(waiter.id);
    if (!group.waiters.size) finishGroup(group);
  };

  const grantWaiter = (group, waiter) => {
    if (waiter.cancelled || waiter.granted) return;
    waiter.granted = true;
    waiter.resolve(() => releaseWaiter(group, waiter));
  };

  const activateGroup = (group) => {
    group.active = true;
    activeGroups.set(group.key, group);
    activeCount += 1;
    group.timeoutId = setTimeout(() => {
      if (activeGroups.get(group.key) !== group) return;
      group.waiters.clear();
      finishGroup(group);
    }, Math.max(1, Number(workTimeoutMs) || 30 * 1000));
    group.waiters.forEach((waiter) => grantWaiter(group, waiter));
  };

  function drain() {
    while (activeCount < concurrency()) {
      // Near-viewport admission and the connection-aware concurrency cap are
      // the real workload controls. Do not additionally pause standard work on
      // document.visibilityState: during reload Chrome can report `hidden`
      // before listeners attach and throttle the very signals needed to resume.
      const group = queues.priority.shift() || queues.standard.shift();
      if (!group) return;
      queuedGroups.delete(group.key);
      if (!group.waiters.size) continue;
      activateGroup(group);
    }
  }

  const request = (rawKey, { priority = false } = {}) => {
    const key = normalizeKey(rawKey);
    if (!key) {
      return { promise: Promise.resolve(() => {}), cancel: () => {}, setPriority: () => {} };
    }

    const id = ++waiterSequence;
    let resolveWaiter;
    const promise = new Promise((resolve) => {
      resolveWaiter = resolve;
    });
    const waiter = {
      id,
      priority: Boolean(priority),
      resolve: resolveWaiter,
      cancelled: false,
      granted: false,
      released: false,
    };

    let group = activeGroups.get(key) || queuedGroups.get(key);
    if (!group) {
      group = { key, priority: Boolean(priority), active: false, waiters: new Map() };
      queuedGroups.set(key, group);
      queues[group.priority ? "priority" : "standard"].push(group);
    } else if (!group.active && priority && !group.priority) {
      removeQueuedGroup(group);
      group.priority = true;
      queuedGroups.set(key, group);
      queues.priority.push(group);
    }
    group.waiters.set(id, waiter);

    if (group.active) {
      grantWaiter(group, waiter);
    } else {
      drain();
    }

    const cancel = () => {
      if (waiter.cancelled || waiter.released) return;
      waiter.cancelled = true;
      if (waiter.granted) {
        releaseWaiter(group, waiter);
        return;
      }
      group.waiters.delete(waiter.id);
      waiter.released = true;
      waiter.resolve(() => {});
      if (!group.waiters.size && queuedGroups.get(group.key) === group) {
        removeQueuedGroup(group);
      }
    };

    const setPriority = (nextPriority) => {
      if (group.active || waiter.cancelled || waiter.released) return;
      waiter.priority = Boolean(nextPriority);
      const groupPriority = Array.from(group.waiters.values()).some((entry) => entry.priority);
      if (group.priority === groupPriority) return;
      removeQueuedGroup(group);
      group.priority = groupPriority;
      queuedGroups.set(group.key, group);
      queues[group.priority ? "priority" : "standard"].push(group);
      drain();
    };

    return { promise, cancel, setPriority };
  };

  const getSnapshot = () => ({
    activeCount,
    activeKeys: Array.from(activeGroups.keys()),
    priorityQueued: queues.priority.map((group) => group.key),
    standardQueued: queues.standard.map((group) => group.key),
  });

  return { request, getSnapshot, resume: drain };
};

export const imageWorkController = createImageWorkController();

if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") imageWorkController.resume();
  });
}

export const __resetImageWorkRegistryForTests = () => {
  completedImages.clear();
  failedImages.clear();
};

export const IMAGE_WORK_COMPLETED_TTL_MS = COMPLETED_IMAGE_TTL_MS;
export const IMAGE_WORK_FAILURE_COOLDOWN_MS = FAILURE_COOLDOWN_MS;
